import { RatingStar } from "@dmm-com/rocket-web-ui-library";
import { useCallback, useState } from "react";
import { NoReview } from "./no-review";
import { ReviewInfo } from "./review-info";
import { ReviewModal } from "./review-modal";
import * as Styled from "./style";
import { FetchMoreLoading } from "~/components/blocks/common/fetch-more-loading";
import { isOpenReviewModalVar } from "~/components/stores/detail-product/store";
import { openDialog } from "~/components/stores/dialog/store";
import { SERVER_FETCH_COUNT } from "~/const/server-fetch-count";
import { useHandleClickByDevice } from "~/hooks/use-handle-click-by-device";
import { useMatchMedia } from "~/hooks/use-match-media";
import { useQueryString } from "~/hooks/use-query-string";
import type { G_GetVideoQuery } from "~/lib/graphql";
import { useGetVideoReviewsQuery, G_VideoSeasonType } from "~/lib/graphql";
import { useAuth } from "~/provider/auth";
import { isBot } from "~/util/is-bot";

type ReviewProps = {
  /** videoクエリを叩いて取得したデータ */
  videoData: G_GetVideoQuery["video"];
};

const FIRST_LIST_NUM = 5;
const LIST_NUM = 20;

/**
 * レビューを表示するコンポーネント
 * - 複数話・舞台・グラビア共通で必ず表示するTabContents
 * @param props {@link ReviewProps}
 */
export const Review = ({ videoData }: ReviewProps) => {
  const { isLoggedIn, execAuth } = useAuth();
  const { season } = useQueryString();
  const { handleClickByDevice } = useHandleClickByDevice();
  const { isSpSize } = useMatchMedia();

  const {
    data: reviewData,
    loading: isReviewLoading,
    fetchMore: reviewFetchMore,
  } = useGetVideoReviewsQuery({
    ssr: isBot(),
    variables: {
      id: season,
      first: isBot() ? SERVER_FETCH_COUNT.REVIEW_BY_VOD_DETAIL : FIRST_LIST_NUM,
    },
  });

  const [isLoadingFetchMore, setIsLoadingFetchMore] = useState(false);

  const handleDropdownIconClicked = useCallback(
    async (hasNextPage: boolean, endCursor: string) => {
      if (!reviewFetchMore) return;
      setIsLoadingFetchMore(true);

      if (!hasNextPage) {
        await reviewFetchMore({
          variables: {
            id: season,
            first: FIRST_LIST_NUM,
          },
        });

        setIsLoadingFetchMore(false);
        return;
      }

      await reviewFetchMore({
        variables: {
          id: season,
          first: LIST_NUM,
          after: endCursor,
        },
      });

      setIsLoadingFetchMore(false);
    },
    [reviewFetchMore, season]
  );

  const handleReviewBtnPressed = useCallback(() => {
    if (!isLoggedIn) {
      openDialog({
        title: "レビュー投稿/編集は\nログイン後にできます",
        description: "ログインしますか？",
        mainButtonName: "ログイン",
        mainButtonLabelText: "ログイン",
        isDisabledAnimation: true,
        onMainButtonAction: execAuth,
      });

      return;
    }

    isOpenReviewModalVar(true);
  }, [isLoggedIn]);

  if (isReviewLoading || reviewData?.video === undefined) return null;

  const {
    reviewSummary: { averagePoint, reviewerCount, reviewCommentCount },
    reviews: {
      edges,
      pageInfo: { hasNextPage, endCursor },
      total,
    },
  } = reviewData.video;

  const isEmptyReviews = total === 0;

  if (isEmptyReviews) {
    return (
      <>
        <NoReview />
        <ReviewModal videoData={videoData} />
      </>
    );
  }

  return (
    <>
      <div>
        <Styled.DivReviewPosition isLastItem={reviewCommentCount > 5 && !hasNextPage}>
          {videoData?.seasonType === G_VideoSeasonType.Legacy && (
            <Styled.DivHeaderSummary>
              <RatingStar starScore={averagePoint} size="XXL" isReadOnly />

              <Styled.DivSpanWrapper>
                <span>平均評価</span>
                <Styled.SpanValue>{averagePoint.toFixed(2)}点</Styled.SpanValue>
              </Styled.DivSpanWrapper>

              <Styled.DivSpanWrapper>
                <span>総評価数</span>
                <Styled.SpanValue>{reviewerCount}</Styled.SpanValue>
                <Styled.SpanCommentCount>（{reviewCommentCount}件のコメント）</Styled.SpanCommentCount>
              </Styled.DivSpanWrapper>
            </Styled.DivHeaderSummary>
          )}

          {edges.map(({ node }, idx) => {
            return <ReviewInfo key={`review-information-${idx}`} reviewData={node} />;
          })}

          {reviewCommentCount > 5 && (
            <Styled.BtnDropdown
              type="button"
              name="Dropdown Button"
              aria-label="Dropdown Button"
              isLastItem={!hasNextPage}
              disabled={isLoadingFetchMore}
              {...handleClickByDevice(() => handleDropdownIconClicked(hasNextPage, endCursor))}
            >
              <FetchMoreLoading isLoading={isLoadingFetchMore} />
              {!isLoadingFetchMore && <Styled.Icon name={hasNextPage ? "arrowDown" : "arrowUp"} size="XXXL" />}
            </Styled.BtnDropdown>
          )}
        </Styled.DivReviewPosition>

        <Styled.DivAddReviewWrapper>
          <Styled.Button
            name="post-review"
            mainText="レビュー投稿 / 編集"
            color="GRAY900"
            fontSize={isSpSize ? "S" : "M"}
            {...handleClickByDevice(handleReviewBtnPressed)}
          />
        </Styled.DivAddReviewWrapper>
      </div>

      <ReviewModal videoData={videoData} />
    </>
  );
};
